/**
 * Index template
 * This file contains logic specific to a template.
 * It is intended to ONLY be loaded on templates that require
 * this specific functionality.
 **/

import Global from './lib/global';
import Logger from './lib/logger';

// Vars
// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

const NAME = 'Index';

let page = 1;
let limit = 9; // 9 posts per page (featured post is separate)
let offset = 1; // Start at offset 1 to skip the featured post

let continueFetching = true;
const themePostGridDom = document.querySelector('[data-theme-post-grid]');
const postType = themePostGridDom ? themePostGridDom.getAttribute('data-theme-post-grid-post-type') || 'post' : 'post';

// Count how many posts are actually loaded initially
const getInitialPostCount = () => {
	if (!themePostGridDom) return 9; // Default to 9 if we can't find the container
	const postsContainer = themePostGridDom.querySelector('.theme-post-grid__posts');
	if (!postsContainer) return 9; // Default to 9 if container not found
	const count = postsContainer.querySelectorAll('.theme-post-grid__post').length;
	return count > 0 ? count : 9; // Default to 9 if count is 0
};

// Calculate initial offset based on actual posts loaded (1 featured + initial grid posts)
// This will be calculated after DOM is ready
let initialPostCount = 0;
let initialOffset = 1; // Default: 1 featured + 0 grid (will be updated)

// Get base URL for pushState updates
const getBaseUrl = () => {
	const url = new URL(window.location.href);
	// Remove existing page parameter if present
	url.searchParams.delete('page');
	return url.toString().split('?')[0];
};
const baseUrl = getBaseUrl();

// Logic
// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

/**
 * init - Initialize the page
 **/
const init = () => {
	Logger.log(`${NAME} > loaded`);

	// Calculate initial post count and offset after DOM is ready
	initialPostCount = getInitialPostCount();
	initialOffset = 1 + initialPostCount; // 1 for featured post + number of grid posts loaded
	Logger.log(`${NAME} > initial post count: ${initialPostCount}, initial offset: ${initialOffset}`);

	// Handle browser back/forward buttons
	if (window.history && window.history.pushState) {
		window.addEventListener('popstate', (event) => {
			if (event.state && event.state.page) {
				// User navigated back/forward - would need to restore scroll position
				// For now, just log it. Full implementation would require saving/restoring state
				Logger.log(`${NAME} > popstate to page ${event.state.page}`);
			}
		});
	}

	initInfiniteScroll();
}

/**
 * initInfiniteScroll - Initialize the infinite scroll
 * @returns {void}
 */
const initInfiniteScroll = async () => {
	Logger.log(`${NAME} > initInfiniteScroll`);

	if (!themePostGridDom) return;

	const sentinel = themePostGridDom.querySelector('[data-theme-post-grid-sentinel]');

	// Set up intersection observer for the bottom of the post grid to be showing
	const observer = new IntersectionObserver((entries) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				if (!continueFetching) return;
				// Fetch the posts
				fetchPosts().then(() => {
					// Re-observe the bottom after new posts are added
					if (continueFetching) {
						observer.observe(sentinel);
					}
				});
				observer.unobserve(entry.target);
			}
		});
	}, {
		// Trigger when the bottom edge is 200px above the viewport bottom
		rootMargin: '0px 0px 200px 0px',
		threshold: 0
	});

	// Wait for the posts container to be loaded, then observe it
	setTimeout(() => {
		observer.observe(sentinel, { childList: true, subtree: true });
	}, 500);
}

/**
 * fetchPosts - Fetch the posts
 * @returns {Promise} - The promise
 */
const fetchPosts = async () => {
	Logger.log(`${NAME} > fetchPosts`);

	// Increment the page
	page++;
	// Calculate offset: initial offset (featured + initial grid) + (page - 2) * limit
	// Page 2 should start after initial posts, page 3 after that, etc.
	offset = initialOffset + (page - 2) * limit;

	Logger.log(`${NAME} > fetching page ${page} with offset ${offset}, limit ${limit}`);

	const formData = new FormData();

	formData.append('action', 'wonder_get_post_grid_items');
	formData.append('nonce', window.global_vars.ajax_nonce);
	formData.append('page', page);
	formData.append('limit', limit);
	formData.append('offset', offset);
	formData.append('post_type', postType);

	const url = `${window.global_vars.ajax_url}`;

	// Fetch from wp-admin
	const response = await fetch(url, {
		method: 'POST',
		body: formData,
	});
	const data = await response.json();

	Logger.log(`${NAME} > received ${data.post_grid_items.length} posts`);

	// If there are no more posts, set continueFetching to false
	if (data.post_grid_items.length === 0) {
		continueFetching = false;
		Logger.log(`${NAME} > no more posts, stopping infinite scroll`);
	}

	// Append the posts to the post grid
	appendPosts(data.post_grid_items);

	// Update URL with pushState for SEO and UX (per Google's recommendations)
	if (window.history && window.history.pushState) {
		const newUrl = page > 1 ? `${baseUrl}?page=${page}` : baseUrl;
		window.history.pushState({ page: page }, '', newUrl);
	}

	return data;
}

/**
 * appendPosts - Append the posts to the post grid
 * @param {Array} posts - The posts to append
 * @returns {void}
 */
const appendPosts = (posts) => {
	Logger.log(`${NAME} > appendPosts`);
	const themePostGridPostsDom = themePostGridDom.querySelector('.theme-post-grid__posts');

	// Loop through each post, wrap in <div class="theme-post-grid__post">, and append to the post grid
	// Use insertAdjacentHTML instead of innerHTML += to avoid re-rendering existing content
	posts.forEach((post) => {
		themePostGridPostsDom.insertAdjacentHTML('beforeend', `<div class="theme-post-grid__post">${post}</div>`);
	});
}

// Bootstrap global functionality
Global();

// Bootstrap page-specific functionality
init();

// Do not edit below this line...
// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒
Logger.log(`${NAME} > loaded`);
